import { useState, useRef, useEffect } from 'react';
import { useGameSocket } from '../hooks/useGameSocket';
import './Buzzer.css';

const TEAM_NAMES = ['Team One', 'Team Two', 'Team Three'];
const TEAM_COLORS = ['#1565c0', '#b71c1c', '#2e7d32'];
const BUZZ_SECONDS = 5;
const LS_KEY = 'gamy_player_name';

interface BuzzerState {
  armed: boolean;
  activeBuzz: { teamIndex: number; at: number; playerName?: string | null } | null;
  wrongTeams: number[];
  answered: boolean;
}

interface ActiveClue {
  category: string;
  value: number;
  clue: string;
  isDailyDouble: boolean;
}

interface Props {
  teamIndex: number;
}

export default function Buzzer({ teamIndex }: Props) {
  const [buzzer, setBuzzer] = useState<BuzzerState | null>(null);
  const [activeClue, setActiveClue] = useState<ActiveClue | null>(null);
  const [round, setRound] = useState<string>('single');
  const [fjPhase, setFjPhase] = useState<string | null>(null);
  const [fjWagerInput, setFjWagerInput] = useState('');
  const [fjWagerSubmitted, setFjWagerSubmitted] = useState<number | null>(null);
  const [fjTimerStartedAt, setFjTimerStartedAt] = useState<number | null>(null);
  const [fjAnswerInput, setFjAnswerInput] = useState('');
  const [fjAnswerSubmitted, setFjAnswerSubmitted] = useState<string | null>(null);
  const [captains, setCaptains] = useState<(string | null)[]>([null, null, null]);
  const [ddWager, setDdWager] = useState<{ teamIndex: number; amount: number | null } | null>(null);
  const [ddWagerInput, setDdWagerInput] = useState('');
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Name registration
  const [playerName, setPlayerName] = useState<string>(() => localStorage.getItem(LS_KEY) ?? '');
  const [nameInput, setNameInput] = useState('');
  const [nameError, setNameError] = useState('');
  const [registered, setRegistered] = useState(false);
  const registeredRef = useRef(false);

  const { send, connected } = useGameSocket((msg) => {
    if (msg.type === 'STATE') {
      setBuzzer(msg.buzzer ?? null);
      setActiveClue(msg.activeClue ?? null);
      setRound(msg.round ?? 'single');
      setCaptains(msg.captains ?? [null, null, null]);
      setDdWager(msg.ddWager ?? null);
      const newFjPhase = msg.fjPhase ?? null;
      setFjPhase(newFjPhase);
      setFjTimerStartedAt(msg.fjTimerStartedAt ?? null);
      // Reset wager submission when wagering phase resets
      if (newFjPhase !== 'wagering' && newFjPhase !== 'question' && newFjPhase !== 'thinking') {
        setFjWagerSubmitted(null);
        setFjWagerInput('');
      }
      // Reset FJ answer state when leaving the answer phase or starting a new round.
      if (newFjPhase !== 'thinking' && newFjPhase !== 'answer') {
        setFjAnswerSubmitted(null);
        setFjAnswerInput('');
      } else {
        // Adopt server-side value if present (e.g. reload mid-thinking).
        const ours = Array.isArray(msg.fjAnswers) ? msg.fjAnswers[teamIndex] : null;
        if (ours != null) setFjAnswerSubmitted(ours);
      }
      // Clear DD wager input when no DD active or this team isn't wagering
      if (!msg.ddWager || msg.ddWager.teamIndex !== teamIndex || msg.ddWager.amount !== null) {
        setDdWagerInput('');
      }
      // Auto-register on first connect AND re-register if a WS reconnect
      // dropped us from the server's roster mid-game. We treat "not in
      // server roster" as authoritative — the server's close handler had
      // already decremented the count, and registeredRef is stale.
      const myName = playerName.trim();
      if (myName) {
        const serverRoster = Array.isArray(msg.rosters?.[teamIndex]) ? msg.rosters[teamIndex] : [];
        const inServerRoster = serverRoster.some((n: string) => n.toLowerCase() === myName.toLowerCase());
        if (!inServerRoster) {
          send({ type: 'BUZZER_REGISTER', teamIndex, playerName: myName });
          registeredRef.current = true;
          setRegistered(true);
        } else if (!registeredRef.current) {
          // Server already knows us (e.g. we reloaded with the same name).
          registeredRef.current = true;
          setRegistered(true);
        }
      }
    }
    if (msg.type === 'REGISTER_ERROR') {
      // Name taken — clear stored name and prompt again
      localStorage.removeItem(LS_KEY);
      setPlayerName('');
      setRegistered(false);
      registeredRef.current = false;
      setNameError(msg.reason ?? 'Name already taken. Choose a different name.');
    }
  });

  // Tick every 100ms while either the buzz timer OR the FJ "Think" timer
  // is active so the visible countdowns update smoothly.
  useEffect(() => {
    const timerActive = !!buzzer?.activeBuzz || (fjPhase === 'thinking' && !!fjTimerStartedAt);
    if (timerActive) {
      timerRef.current = setInterval(() => setNow(Date.now()), 100);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [buzzer?.activeBuzz?.at, fjPhase, fjTimerStartedAt]);

  function submitName(e: React.FormEvent) {
    e.preventDefault();
    const n = nameInput.trim();
    if (!n) return;
    setNameError('');
    localStorage.setItem(LS_KEY, n);
    setPlayerName(n);
    registeredRef.current = false; // will register on next message
    send({ type: 'BUZZER_REGISTER', teamIndex, playerName: n });
    registeredRef.current = true;
    setRegistered(true);
  }

  function handleBuzz() {
    send({ type: 'BUZZ', teamIndex, playerName: playerName.trim() || null });
  }

  function submitFjWager(e: React.FormEvent) {
    e.preventDefault();
    const amount = parseInt(fjWagerInput.replace(/\D/g, ''), 10);
    if (isNaN(amount) || amount < 0) return;
    send({ type: 'FJ_WAGER', teamIndex, amount });
    setFjWagerSubmitted(amount);
  }

  function submitDdWager(e: React.FormEvent) {
    e.preventDefault();
    const amount = parseInt(ddWagerInput.replace(/\D/g, ''), 10);
    if (isNaN(amount) || amount < 0) return;
    send({ type: 'DD_WAGER', amount });
  }

  function submitFjAnswer(e: React.FormEvent) {
    e.preventDefault();
    const answer = fjAnswerInput.trim();
    if (!answer) return;
    send({ type: 'FJ_ANSWER', teamIndex, answer });
    setFjAnswerSubmitted(answer);
  }

  const teamColor = TEAM_COLORS[teamIndex] ?? '#1565c0';
  const teamName = TEAM_NAMES[teamIndex] ?? `Team ${teamIndex + 1}`;
  const captainName = captains[teamIndex] ?? null;
  const isCaptain = !!playerName.trim() && captainName === playerName.trim();
  const ddIsForMyTeam = ddWager?.teamIndex === teamIndex;
  const ddAwaitingWager = !!ddWager && ddWager.amount === null;

  const isLockedOut = buzzer?.wrongTeams.includes(teamIndex) ?? false;
  const myBuzz = buzzer?.activeBuzz?.teamIndex === teamIndex;
  const otherBuzz = !!buzzer?.activeBuzz && !myBuzz;
  const canBuzz = !!(buzzer?.armed && !isLockedOut && !myBuzz && !buzzer?.activeBuzz);
  const answered = buzzer?.answered ?? false;

  const buzzingPlayerName = buzzer?.activeBuzz != null
    ? (buzzer.activeBuzz.playerName || TEAM_NAMES[buzzer.activeBuzz.teamIndex] || `Team ${buzzer.activeBuzz.teamIndex + 1}`)
    : null;

  const buzzTimeLeft = buzzer?.activeBuzz
    ? Math.max(0, BUZZ_SECONDS - (now - buzzer.activeBuzz.at) / 1000)
    : null;
  const buzzExpired = buzzTimeLeft !== null && buzzTimeLeft <= 0;

  function statusText() {
    if (answered) return 'Answer Revealed';
    if (myBuzz) return `${playerName || 'YOU'} BUZZED IN!`;
    if (isLockedOut) return 'LOCKED OUT';
    if (otherBuzz) return `${buzzingPlayerName} buzzed in`;
    if (canBuzz) return 'Buzzers Armed';
    return 'Waiting…';
  }

  function statusClass() {
    if (myBuzz) return 'bz-status-active';
    if (isLockedOut || otherBuzz) return 'bz-status-locked';
    if (canBuzz) return 'bz-status-ready';
    return 'bz-status-waiting';
  }

  // Show name entry form if no name registered yet
  if (!playerName.trim() || !registered) {
    return (
      <div className="bz-page" style={{ '--team-color': teamColor } as any}>
        <div className="bz-header">
          <span className="bz-team-name">{teamName}</span>
          <span className={`bz-conn ${connected ? 'bz-conn-ok' : 'bz-conn-bad'}`}>
            {connected ? '●' : '○'}
          </span>
        </div>
        <div className="bz-name-form-wrap">
          <div className="bz-name-title">Enter Your Name</div>
          <div className="bz-name-subtitle">So the host knows who buzzed in</div>
          {nameError && <div className="bz-name-error">{nameError}</div>}
          <form className="bz-name-form" onSubmit={submitName}>
            <input
              className="bz-name-input"
              type="text"
              placeholder="Your first name"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              maxLength={24}
              autoFocus
              autoComplete="off"
            />
            <button type="submit" className="bz-name-submit" disabled={!nameInput.trim()}>
              Join Game →
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Daily Double wager screen — only shown to the captain of the wagering team
  if (activeClue?.isDailyDouble && ddIsForMyTeam && ddAwaitingWager) {
    if (!isCaptain) {
      return (
        <div className="bz-page" style={{ '--team-color': teamColor } as any}>
          <div className="bz-header">
            <span className="bz-team-name">{teamName}</span>
            <span className={`bz-conn ${connected ? 'bz-conn-ok' : 'bz-conn-bad'}`}>
              {connected ? '●' : '○'}
            </span>
          </div>
          <div className="bz-player-badge">{playerName}</div>
          <div className="bz-fj-wrap">
            <div className="bz-fj-title">★ Daily Double</div>
            <div className="bz-fj-subtitle">
              {captainName ? `${captainName} (captain) is wagering…` : 'Captain is wagering…'}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="bz-page" style={{ '--team-color': teamColor } as any}>
        <div className="bz-header">
          <span className="bz-team-name">{teamName}</span>
          <span className={`bz-conn ${connected ? 'bz-conn-ok' : 'bz-conn-bad'}`}>
            {connected ? '●' : '○'}
          </span>
        </div>
        <div className="bz-player-badge">{playerName} ★ Captain</div>
        <div className="bz-fj-wrap">
          <div className="bz-fj-title">★ Daily Double</div>
          <div className="bz-fj-subtitle">Enter your wager</div>
          <form className="bz-fj-form" onSubmit={submitDdWager}>
            <input
              className="bz-fj-input"
              type="number"
              min="0"
              placeholder="$0"
              value={ddWagerInput}
              onChange={e => setDdWagerInput(e.target.value)}
              autoFocus
            />
            <button
              type="submit"
              className="bz-fj-submit"
              disabled={!ddWagerInput.trim()}
            >
              Lock In Wager
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Final Jeopardy thinking screen — timer for everyone, plus an answer
  // input form for the team captain (can resubmit/change until time's up).
  if (round === 'final' && fjPhase === 'thinking' && fjTimerStartedAt) {
    const elapsed = (now - fjTimerStartedAt) / 1000;
    const fjTimeLeft = Math.max(0, 30 - elapsed);
    const fjExpired = fjTimeLeft <= 0;
    const urgent = fjTimeLeft <= 10;
    const timerClasses = `bz-timer ${fjExpired ? 'bz-timer-expired' : urgent ? 'bz-timer-urgent' : ''} bz-timer-other`;
    return (
      <div className="bz-page" style={{ '--team-color': teamColor } as any}>
        <div className="bz-header">
          <span className="bz-team-name">{teamName}</span>
          <span className={`bz-conn ${connected ? 'bz-conn-ok' : 'bz-conn-bad'}`}>
            {connected ? '●' : '○'}
          </span>
        </div>
        <div className="bz-player-badge">{playerName}{isCaptain ? ' ★ Captain' : ''}</div>
        <div className="bz-fj-wrap">
          <div className="bz-fj-title">Final Jeopardy</div>
          <div className={timerClasses}>
            {fjExpired ? "TIME'S UP" : `${fjTimeLeft.toFixed(1)}s`}
          </div>
          {isCaptain ? (
            fjAnswerSubmitted !== null ? (
              <div className="bz-fj-submitted">
                <div className="bz-fj-submitted-label">Answer locked</div>
                <div className="bz-fj-answer-text">{fjAnswerSubmitted}</div>
                {!fjExpired && (
                  <button
                    className="bz-fj-change-btn"
                    onClick={() => { setFjAnswerSubmitted(null); setFjAnswerInput(''); }}
                  >
                    Change answer
                  </button>
                )}
              </div>
            ) : (
              <form className="bz-fj-form" onSubmit={submitFjAnswer}>
                <input
                  className="bz-fj-input bz-fj-answer-input"
                  type="text"
                  placeholder="What is…?"
                  value={fjAnswerInput}
                  onChange={e => setFjAnswerInput(e.target.value)}
                  maxLength={240}
                  autoFocus
                  disabled={fjExpired}
                />
                <button
                  type="submit"
                  className="bz-fj-submit"
                  disabled={!fjAnswerInput.trim() || fjExpired}
                >
                  Lock In Answer
                </button>
              </form>
            )
          ) : (
            <div className="bz-fj-subtitle">
              {captainName ? `${captainName} is writing the team's answer…` : 'Captain is writing…'}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Final Jeopardy review/answer screen — buzzers idle while host walks
  // through each team's response on the board.
  if (round === 'final' && (fjPhase === 'review' || fjPhase === 'answer')) {
    return (
      <div className="bz-page" style={{ '--team-color': teamColor } as any}>
        <div className="bz-header">
          <span className="bz-team-name">{teamName}</span>
          <span className={`bz-conn ${connected ? 'bz-conn-ok' : 'bz-conn-bad'}`}>
            {connected ? '●' : '○'}
          </span>
        </div>
        <div className="bz-player-badge">{playerName}{isCaptain ? ' ★ Captain' : ''}</div>
        <div className="bz-fj-wrap">
          <div className="bz-fj-title">Final Jeopardy</div>
          <div className="bz-fj-subtitle">
            {fjPhase === 'review' ? 'Reviewing answers — watch the game screen' : 'Answer revealed'}
          </div>
        </div>
      </div>
    );
  }

  // Final Jeopardy wagering screen — only the captain can submit; others see status
  if (round === 'final' && (fjPhase === 'wagering' || (fjPhase === null && !buzzer?.armed))) {
    if (!isCaptain) {
      return (
        <div className="bz-page" style={{ '--team-color': teamColor } as any}>
          <div className="bz-header">
            <span className="bz-team-name">{teamName}</span>
            <span className={`bz-conn ${connected ? 'bz-conn-ok' : 'bz-conn-bad'}`}>
              {connected ? '●' : '○'}
            </span>
          </div>
          <div className="bz-player-badge">{playerName}</div>
          <div className="bz-fj-wrap">
            <div className="bz-fj-title">Final Jeopardy</div>
            <div className="bz-fj-subtitle">
              {captainName ? `${captainName} (captain) is wagering for the team…` : 'Captain is wagering…'}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="bz-page" style={{ '--team-color': teamColor } as any}>
        <div className="bz-header">
          <span className="bz-team-name">{teamName}</span>
          <span className={`bz-conn ${connected ? 'bz-conn-ok' : 'bz-conn-bad'}`}>
            {connected ? '●' : '○'}
          </span>
        </div>
        <div className="bz-player-badge">{playerName} ★ Captain</div>
        <div className="bz-fj-wrap">
          <div className="bz-fj-title">Final Jeopardy</div>
          <div className="bz-fj-subtitle">Enter your wager</div>
          {fjWagerSubmitted !== null ? (
            <div className="bz-fj-submitted">
              <div className="bz-fj-submitted-label">Wager submitted</div>
              <div className="bz-fj-submitted-amount">${fjWagerSubmitted.toLocaleString()}</div>
              <button className="bz-fj-change-btn" onClick={() => setFjWagerSubmitted(null)}>
                Change wager
              </button>
            </div>
          ) : (
            <form className="bz-fj-form" onSubmit={submitFjWager}>
              <input
                className="bz-fj-input"
                type="number"
                min="0"
                placeholder="$0"
                value={fjWagerInput}
                onChange={e => setFjWagerInput(e.target.value)}
                autoFocus
              />
              <button
                type="submit"
                className="bz-fj-submit"
                disabled={!fjWagerInput.trim()}
              >
                Lock In Wager
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bz-page" style={{ '--team-color': teamColor } as any}>
      <div className="bz-header">
        <span className="bz-team-name">{teamName}</span>
        <span className={`bz-conn ${connected ? 'bz-conn-ok' : 'bz-conn-bad'}`}>
          {connected ? '●' : '○'}
        </span>
      </div>

      <div className="bz-player-badge">{playerName}{isCaptain ? ' ★ Captain' : ''}</div>

      {/* Active clue display */}
      {activeClue && (
        <div className="bz-clue-card">
          <div className="bz-clue-meta">
            <span className="bz-clue-cat">{activeClue.category}</span>
            {!activeClue.isDailyDouble && (
              <span className="bz-clue-val">${activeClue.value.toLocaleString()}</span>
            )}
          </div>
          <div className="bz-clue-text">
            {activeClue.isDailyDouble ? '★ DAILY DOUBLE' : activeClue.clue}
          </div>
        </div>
      )}

      <div className={`bz-status ${statusClass()}`}>{statusText()}</div>

      {buzzTimeLeft !== null && (
        <div className={`bz-timer ${buzzExpired ? 'bz-timer-expired' : buzzTimeLeft < 2.5 ? 'bz-timer-urgent' : ''} ${!myBuzz ? 'bz-timer-other' : ''}`}>
          {buzzExpired ? "TIME'S UP" : `${buzzTimeLeft.toFixed(1)}s`}
        </div>
      )}

      <button
        className={`bz-btn ${canBuzz ? 'bz-btn-active' : 'bz-btn-disabled'}`}
        disabled={!canBuzz}
        onClick={handleBuzz}
        aria-label="Buzz in"
      >
        BUZZ
      </button>

      <button className="bz-change-name-btn" onClick={() => {
        localStorage.removeItem(LS_KEY);
        setPlayerName('');
        setNameInput('');
        setNameError('');
        setRegistered(false);
        registeredRef.current = false;
      }}>
        Change name
      </button>
    </div>
  );
}
